import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StripeErrorMappingService {
  
  private readonly errorMappings: { [key: string]: string } = {
    // Card declined errors
    'card_declined': 'Your card was declined. Please use a different card.',
    'expired_card': 'Your card has expired. Please use a valid card.',
    'incorrect_cvc': 'The CVC number is incorrect.',
    'incorrect_number': 'The card number is incorrect.',
    'processing_error': 'An error occurred while processing the card. Try again.',
    'insufficient_funds': 'The card has insufficient funds.',
    'lost_card': 'This card has been reported lost. Use a different card.',
    'stolen_card': 'This card has been reported stolen.',
    'incorrect_zip': 'The ZIP/postal code is incorrect.',
    'authentication_required': 'Authentication is required for this transaction. Please verify with your bank.',
    'do_not_honor': 'Your bank declined the transaction. Contact your bank or try a different card.',
    'currency_not_supported': 'This card does not support charges in the selected currency.',
    'fraudulent': 'This transaction was flagged as fraudulent.',
    'issuer_not_available': 'Your card issuer could not be reached. Try again later.',
    'duplicate_transaction': 'This appears to be a duplicate transaction. Please wait and try again.',
    'card_not_supported': 'This card is not supported. Please use another card.',
    'amount_too_large': 'The amount is too large to process on this card.',
    'amount_too_small': 'The amount is too small to charge.',
    
    // Bank account errors
    'invalid_routing_number': 'The routing number you entered is invalid. Please check with your bank.',
    'invalid_account_number': 'The account number you entered is invalid. Please check it and try again.',
    'account_already_exists': 'This bank account is already on file.',
    'bank_account_tokenization_failed': 'Something went wrong while saving your bank information. Please try again.',
    'invalid_account_country': 'This bank account cannot be used in the selected country.',
    
    // Card validation errors
    'invalid_expiry_month': 'The card\'s expiration month is invalid.',
    'invalid_expiry_year': 'The card\'s expiration year is invalid.',
    'postal_code_invalid': 'The postal code entered is not valid.',
    'zip_check_failed': 'The ZIP code does not match your billing address.',
    'avs_check_failed': 'Your billing address could not be verified. Please confirm and try again.',
    'cvc_check_failed': 'The CVC could not be verified. Please double-check your card.',
    
    // System errors
    'gateway_error': 'We\'re having trouble reaching your payment gateway. Please try again shortly.',
    'rate_limit_error': 'Too many attempts. Please wait and try again.',
    'network_error': 'A network error occurred. Please check your connection and retry.',
    'card_velocity_exceeded': 'You\'ve exceeded the number of allowed transactions. Please wait or use another card.',
    'too_many_attempts': 'You\'ve made too many attempts. Please try again later.',
    'api_rate_limit_exceeded': 'Too many attempts from our side. Please retry after a few seconds.'
  };

  private readonly genericErrorMessage = 'Quote ineligible for acceptance';

  /**
   * Maps Stripe error codes to user-friendly messages
   * @param error - The error object from Stripe or backend
   * @returns User-friendly error message
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getErrorMessage(error: any): string {
    if (!error) {
      return this.genericErrorMessage;
    }

    // Check for stripe_error_code first (backend specific field)
    if (error.stripe_error_code && this.errorMappings[error.stripe_error_code]) {
      return this.errorMappings[error.stripe_error_code];
    }

    // Check for Stripe error code
    if (error.code && this.errorMappings[error.code]) {
      return this.errorMappings[error.code];
    }

    // Check for decline code
    if (error.decline_code && this.errorMappings[error.decline_code]) {
      return this.errorMappings[error.decline_code];
    }

    // Check if error is nested in response structure
    if (error.error) {
      const nestedError = error.error;
      if (nestedError.stripe_error_code && this.errorMappings[nestedError.stripe_error_code]) {
        return this.errorMappings[nestedError.stripe_error_code];
      }
      if (nestedError.code && this.errorMappings[nestedError.code]) {
        return this.errorMappings[nestedError.code];
      }
      if (nestedError.decline_code && this.errorMappings[nestedError.decline_code]) {
        return this.errorMappings[nestedError.decline_code];
      }
    }

    // Check for error in nested structure (common in HTTP responses)
    if (error.body && error.body.error) {
      const bodyError = error.body.error;
      if (bodyError.stripe_error_code && this.errorMappings[bodyError.stripe_error_code]) {
        return this.errorMappings[bodyError.stripe_error_code];
      }
      if (bodyError.code && this.errorMappings[bodyError.code]) {
        return this.errorMappings[bodyError.code];
      }
      if (bodyError.decline_code && this.errorMappings[bodyError.decline_code]) {
        return this.errorMappings[bodyError.decline_code];
      }
    }

    // Check for error in response data
    if (error.data && error.data.error) {
      const dataError = error.data.error;
      if (dataError.stripe_error_code && this.errorMappings[dataError.stripe_error_code]) {
        return this.errorMappings[dataError.stripe_error_code];
      }
      if (dataError.code && this.errorMappings[dataError.code]) {
        return this.errorMappings[dataError.code];
      }
      if (dataError.decline_code && this.errorMappings[dataError.decline_code]) {
        return this.errorMappings[dataError.decline_code];
      }
    }

    // Fallback to existing message if available
    if (error.message) {
      return error.message;
    }

    if (error.error && typeof error.error === 'string') {
      return error.error;
    }

    // Final fallback
    return this.genericErrorMessage;
  }
}
